import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import type { startseite, wahl } from "@progressive-development/politik-model";

import { blockStyles } from "../../../common/styles/block-styles.js";
import { auswahlZeile, stadtweitZurueck } from "../../../common/components/block-parts.js";
import { navigateOnClick } from "../../../common/router/app-navigation.js";
import { zahl, prozentGenau, datumKurzKlartext } from "../../../common/format/de-format.js";
import { STADT_NAME } from "../../../config/app-config.js";
import { ROUTE_BEZIRK } from "../../../config/navigation.js";
import { AUSWAHL_WAHL } from "../../home/pages/default-home/sections/auswahl-texte.js";
import { juengsteTeilwahlen, teilwahl } from "../wahl-vergleich.js";
import { kurzfassung } from "../wahl-kurzfassung.js";
import {
  KOMMUNALE_ARTEN,
  TEILWAHL_ARTEN,
  teilwahlBeschriftung,
  teilwahlRang,
} from "../wahl-arten.js";
import {
  ART_BEZIRKSVERTRETUNG,
  ortVon,
  uebersichtPfad,
  vorigeWahl,
  wahlEintragVon,
  wahlPfad,
} from "../wahl-auswahl.js";
import type { Fusslink, Verweis } from "../components/app-wahl-verweiszeile.js";

import "../components/app-wahl-ergebnisliste.js";
import "../components/app-wahl-verweiszeile.js";

/**
 * Der Wahlblock eines Gebiets — EIN Bauteil für Startseite, Bezirks- und
 * Stadtteilseite (Fachkonzept 4 und 5, Zielbild 11.3).
 *
 * Die Achsen sind fest: der Ort ist das übergebene Gebiet, der Zeitraum ist
 * die aktuelle Wahl, die Art ist ein Ausschnitt (`arten`). Der Block zeigt je
 * Art die Kurzfassung — Beteiligung · drei stärkste · weitere — und darunter
 * die Verweiszeile auf die übrigen Arten. Er ist ein Ausschnitt, kein
 * Werkzeug: Nichts hier schaltet um; alles Weitere führt ins Kapitel.
 *
 * DER UNTERSCHIED ZWISCHEN DEN SEITEN IST EINER DER DATEN, NICHT DES
 * VERHALTENS:
 *
 * - Startseite: `arten = ["rat"]`, ohne `stadt` → keine Klammerwerte, mit
 *   Balken-Animation, mit Rückweg (`gefiltert`), ohne Weg zur vorigen Wahl.
 * - Bezirksseite: Rat und BV, `stadt` → Klammerwerte an der Ratswahl, dazu
 *   der Weg zur vorigen Wahl. KEINE Sitze: Die stehen als Punktliste in der
 *   Karte „Wer entscheidet hier" — sie gehören dem Gremium; eine dritte
 *   Balkengrafik hier bräche aus.
 * - Stadtteilseite: Rat aus dem eigenen Eintrag, die BV aus dem Eintrag des
 *   BEZIRKS (`bezirk`) — die Quelle veröffentlicht sie nur je Stimmbezirk,
 *   und die Hälfte davon sind Briefwahlbezirke ohne Ort; eine Stadtteil-Summe
 *   wäre eine halbe Wahrheit. Keine Sitze: Die werden je Bezirk vergeben.
 *
 * Kein `wenn Bezirk, dann …`: Wer eine Angabe nicht übergibt, bekommt sie
 * nicht angezeigt.
 *
 * WELCHE WAHL die aktuelle ist, entscheidet `teilwahl()` über den Schlüssel
 * der Pipeline — nie dieses Bauteil. Der Stadtvergleich steht als Klammerwert
 * daneben, ohne Differenz: Der Raumvergleich fragt nach Lage, nicht nach
 * Veränderung (6.5). Die Historie beginnt eine Ebene tiefer — auf der
 * Detailseite, zu der der Fußlink führt.
 */
@customElement("app-wahl-gebietsblock")
@localized()
export class AppWahlGebietsblock extends LitElement {
  /** Das Gebiet — trägt seine Wahlzusammenfassungen (`wahl[]`). */
  @property({ attribute: false }) gebiet?: startseite.GebietEintrag;

  /**
   * Der Bezirk eines Stadtteils — Quelle für die Bezirksvertretungswahl, die
   * es nur je Bezirk gibt. Fehlt er (Bezirksseite, Startseite), kommt die BV
   * aus `gebiet` selbst oder gar nicht.
   */
  @property({ attribute: false }) bezirk?: startseite.GebietEintrag;

  /** Die Gesamtstadt als Bezugswert. Fehlt sie, gibt es keine Klammerwerte. */
  @property({ attribute: false }) stadt?: startseite.GebietEintrag;

  /** Welche Teilwahl je Art die aktuelle ist — die Regel der Pipeline. */
  @property({ attribute: false }) aktuell?: wahl.AktuelleWahlen;

  /** Das Wahlen-Verzeichnis — Anzeigename und Slug der Wahl, die vorige Wahl. */
  @property({ attribute: false }) wahlen: startseite.WahlEintrag[] = [];

  @property({ attribute: false }) parteien: startseite.ParteiEintrag[] = [];

  /** Welche Arten der Block ZEIGT; die übrigen stehen in der Verweiszeile. */
  @property({ attribute: false }) arten: string[] = ["rat"];

  /**
   * Welche Arten die Verweiszeile als Wege anbietet — eine LISTE, die die
   * einbindende Seite setzt. Der Block entscheidet hier nichts: Was er
   * verweist, steht an der Stelle, die die Seite kennt.
   *
   * Vorgabe sind die kommunalen Arten. Seit Bundestag und Europa im Bestand
   * sind, stünden sonst überall sieben Wege statt vier — auf der Startseite
   * würde aus einem Einstieg ein Inhaltsverzeichnis.
   */
  @property({ attribute: false }) verweisArten: string[] = [...KOMMUNALE_ARTEN];

  /** Startseite: Balken wachsen beim Sichtbarwerden. */
  @property({ type: Boolean }) animiert = false;

  /** Startseite: ein Gebiet ist gemerkt — der Block trägt den Rückweg. */
  @property({ type: Boolean }) gefiltert = false;

  /** Gebietsseiten: der leise Fußlink zur vorigen Wahl (1.4). Die Startseite nicht (5.3). */
  @property({ type: Boolean }) mitVorwahlLink = false;

  static override styles = [
    blockStyles,
    css`
      .kopf {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: var(--portal-space-4);
        flex-wrap: wrap;
      }
      .wahl-name {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        text-transform: uppercase;
        letter-spacing: var(--portal-tracking-caps);
      }

      .teilwahlen {
        display: grid;
        gap: var(--portal-space-8);
        margin-top: var(--portal-space-6);
      }
      /* Zwei Teilwahlen nebeneinander, wo Platz ist — sonst untereinander. */
      @media (min-width: 60rem) {
        .teilwahlen.zwei {
          grid-template-columns: 1fr 1fr;
        }
      }

      .teilwahl-titel {
        margin: 0;
        font-family: var(--portal-font-display);
        font-stretch: var(--portal-display-stretch);
        font-weight: var(--portal-display-weight);
        font-size: var(--portal-size-md);
      }
      .beteiligung {
        margin: var(--portal-space-1) 0 var(--portal-space-4);
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        font-variant-numeric: tabular-nums;
      }
      .beteiligung strong {
        font-weight: 500;
        color: var(--portal-ink);
      }
      .hinweis {
        margin: var(--portal-space-3) 0 0;
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        max-width: 60ch;
      }
      .hinweis a {
        color: inherit;
      }
      app-wahl-verweiszeile {
        display: block;
        margin-top: var(--portal-space-6);
      }
    `,
  ];

  /**
   * Der Gebietsname für die Überschrift.
   *
   * Das Verzeichnis führt die Gesamtstadt unter ihrem amtlichen Namen („Stadt
   * Köln"); im Fließtext einer Überschrift steht der Name, unter dem die Stadt
   * im Portal vorkommt (`STADT_NAME`). Entschieden an der EBENE, nie am Namen.
   */
  private get _titelGebiet(): string {
    return !this.gebiet || this.gebiet.ebene === "gesamtstadt" ? STADT_NAME : this.gebiet.name;
  }

  /** Die Wahl beim Namen — kuratiert im Verzeichnis; sonst das Kürzel, sichtbar. */
  private _wahlName(w: startseite.Wahlzusammenfassung | undefined): string {
    return wahlEintragVon(this.wahlen, w?.wahl)?.anzeigename ?? w?.wahl ?? "";
  }

  /**
   * Woher eine Art kommt: die BV aus dem Bezirk, alles andere aus dem Gebiet.
   * Führt das Gebiet die BV selbst (Bezirksseite), hat es Vorrang.
   */
  private _quelleFuer(art: string): startseite.GebietEintrag | undefined {
    if (art !== ART_BEZIRKSVERTRETUNG) return this.gebiet;
    return teilwahl(this.gebiet, art, this.aktuell) ? this.gebiet : this.bezirk;
  }

  private _teilwahlTitel(w: startseite.Wahlzusammenfassung): string {
    const art = teilwahlBeschriftung(w.art) ?? w.teilwahl;
    if (w.art !== ART_BEZIRKSVERTRETUNG) return art;
    /*
     * Die Bezirksvertretung nennt IHREN BEZIRK — nie das Gebiet, aus dem die
     * Zahlen kommen. Seit die Pipeline die BV auch je Stadtteil liefert, sind
     * das zwei verschiedene Dinge: Die Zahlen sind die von Bickendorf, gewählt
     * wird die Bezirksvertretung Ehrenfeld. Stünde hier die Quelle, behauptete
     * der Titel eine „Bezirksvertretung Bickendorf", die es nicht gibt.
     *
     * Ohne bekannten Bezirk bleibt es bei der Art — lieber unbenannt als
     * falsch benannt.
     */
    const bezirkName =
      this.bezirk?.name ?? (this.gebiet?.ebene === "stadtbezirk" ? this.gebiet.name : undefined);
    return bezirkName
      ? msg(str`Bezirksvertretung ${bezirkName}`, { id: "app.wahl.block.bvTitel" })
      : art;
  }

  private _renderBeteiligung(
    w: startseite.Wahlzusammenfassung,
    stadtW?: startseite.Wahlzusammenfassung,
  ) {
    if (w.wahlbeteiligung === undefined) return nothing;
    const stadt =
      stadtW?.wahlbeteiligung !== undefined
        ? html` <span
            >(${msg(str`${STADT_NAME} ${prozentGenau(stadtW.wahlbeteiligung)}`, {
              id: "app.wahl.block.beteiligungStadt",
            })})</span
          >`
        : nothing;
    const von =
      w.waehler !== undefined && w.wahlberechtigte !== undefined
        ? html` ·
          ${msg(str`${zahl(w.waehler)} von ${zahl(w.wahlberechtigte)} Wahlberechtigten`, {
            id: "app.wahl.block.von",
          })}`
        : nothing;
    return html`<p class="beteiligung">
      ${msg("Beteiligung", { id: "app.wahl.block.beteiligung" })}
      <strong>${prozentGenau(w.wahlbeteiligung)}</strong>${stadt}${von}
      ${w.datum ? html` · ${datumKurzKlartext(w.datum)}` : nothing}
    </p>`;
  }

  /**
   * Die BV auf der Stadtteilseite: Der Hinweis sagt, dass die Zahlen dem
   * ganzen Bezirk gehören — Stimmen wie Sitze — und führt dorthin.
   */
  private _renderBezirksHinweis(quelle: startseite.GebietEintrag) {
    if (quelle === this.gebiet) return nothing;
    const ziel = `/${ROUTE_BEZIRK}/${quelle.slug}`;
    return html`<p class="hinweis">
      ${msg(
        str`Die Bezirksvertretung wird für den ganzen Bezirk gewählt — Stimmen und Sitze gibt es nur für ${quelle.name}.`,
        { id: "app.wahl.block.bvBezirk" },
      )}
      <a href=${ziel} @click=${(e: Event) => navigateOnClick(e, ziel)}
        >${msg(str`Zum Stadtbezirk ${quelle.name}`, { id: "app.wahl.block.zumBezirk" })} →</a
      >
    </p>`;
  }

  /** Eine Teilwahl als Spalte. Ohne Ergebnis erscheint sie gar nicht. */
  private _renderTeilwahl(art: string) {
    const quelle = this._quelleFuer(art);
    const w = teilwahl(quelle, art, this.aktuell);
    if (!w?.staerkste?.length || !quelle) return nothing;
    // Die BV-Wahl hat keinen stadtweiten Wert — jeder Bezirk wählt seine eigene.
    const stadtW =
      art === ART_BEZIRKSVERTRETUNG ? undefined : teilwahl(this.stadt, art, this.aktuell);
    const k = kurzfassung(w, stadtW);

    return html`
      <div>
        <h3 class="teilwahl-titel">${this._teilwahlTitel(w)}</h3>
        ${this._renderBeteiligung(w, stadtW)}
        <app-wahl-ergebnisliste
          .zeilen=${k.zeilen}
          .weitere=${k.weitere}
          .parteien=${this.parteien}
          .bezug=${w.gueltigeStimmen}
          klammerName=${stadtW ? STADT_NAME : ""}
          ?animiert=${this.animiert}
        ></app-wahl-ergebnisliste>
        ${
          w.aggregiert
            ? html`<p class="hinweis">
                ${msg(
                  str`Nicht amtlich auf dieser Ebene veröffentlicht — gerechnet: ${w.methode ?? ""}`,
                  { id: "app.wahl.block.aggregiert" },
                )}
              </p>`
            : nothing
        }
        ${this._renderBezirksHinweis(quelle)}
      </div>
    `;
  }

  /** Die Arten, die der Block tatsächlich zeigen kann — mit Ergebnis. */
  private _gezeigt(): string[] {
    return this.arten.filter(
      (a) => teilwahl(this._quelleFuer(a), a, this.aktuell)?.staerkste?.length,
    );
  }

  /**
   * Die Verweiszeile: gezeigte Arten gesetzt, die übrigen als Links auf die
   * Detailseite — mit DIESEM Ort vorgewählt (4.3 C). Nur Arten mit aktueller
   * Teilwahl und bekannter Beschriftung: Eine Art ohne aktuellen Eintrag
   * (keine Stichwahl nötig) fehlt in der Zeile.
   */
  private _verweise(gezeigt: string[]): Verweis[] {
    const ort = ortVon(this.gebiet);
    return juengsteTeilwahlen(this.gebiet, this.aktuell)
      .filter((w) => w.art && this.verweisArten.includes(w.art) && teilwahlBeschriftung(w.art))
      .sort((a, b) => teilwahlRang(a.art) - teilwahlRang(b.art))
      .map((w) => {
        const art = w.art as string;
        const slug = wahlEintragVon(this.wahlen, w.wahl)?.slug;
        return {
          schluessel: art,
          beschriftung: teilwahlBeschriftung(art) as string,
          // Ohne Slug der Wahl gibt es keine Adresse — dann kein Link.
          pfad: gezeigt.includes(art) || !slug ? undefined : wahlPfad({ wahl: slug, art, ...ort }),
        };
      });
  }

  /**
   * Die Fußlinks: ins Kapitel — und auf den Gebietsseiten zur vorigen Wahl,
   * derselbe Ort, dieselbe Art wie die erste gezeigte (1.4). Kein Verlauf,
   * genau eine vorige Wahl.
   */
  private _fusslinks(erste: startseite.Wahlzusammenfassung | undefined): Fusslink[] {
    const links: Fusslink[] = [];
    if (this.mitVorwahlLink && erste?.art) {
      const vorige = vorigeWahl(this.wahlen, erste.wahl, erste.art);
      if (vorige) {
        links.push({
          text: msg(str`${vorige.anzeigename} ansehen`, { id: "app.wahl.block.vorwahl" }),
          pfad: wahlPfad({ wahl: vorige.slug, art: erste.art, ...ortVon(this.gebiet) }),
        });
      }
    }
    links.push({
      /*
       * „Alle Wahlen" war doppeldeutig — es klang nach „alle Wahlen dieses
       * Gebiets", führt aber ins Kapitel. Der Satz nennt jetzt Ziel, Umfang
       * und Ort.
       */
      text: msg(str`Zur Übersicht aller Wahlen in ${STADT_NAME}`, {
        id: "app.wahl.block.zurUebersicht",
      }),
      pfad: uebersichtPfad(),
    });
    return links;
  }

  override render() {
    const gezeigt = this._gezeigt();
    if (gezeigt.length === 0) return this._renderLeer();
    const erste = teilwahl(this._quelleFuer(gezeigt[0]), gezeigt[0], this.aktuell);

    return html`
      <div class="kopf">
        <h2>${msg(str`So hat ${this._titelGebiet} gewählt`, { id: "app.wahl.block.titel" })}</h2>
        <span class="wahl-name">${this._wahlName(erste)}</span>
      </div>
      ${auswahlZeile(AUSWAHL_WAHL())}

      <div class="teilwahlen ${gezeigt.length > 1 ? "zwei" : ""}">
        ${TEILWAHL_ARTEN.filter((a) => gezeigt.includes(a)).map((a) => this._renderTeilwahl(a))}
      </div>

      <app-wahl-verweiszeile
        .eintraege=${this._verweise(gezeigt)}
        .fusslinks=${this._fusslinks(erste)}
      ></app-wahl-verweiszeile>
      ${stadtweitZurueck(this.gefiltert)}
    `;
  }

  /** Kein Ergebnis für dieses Gebiet — der Block sagt es, statt zu verschwinden. */
  private _renderLeer() {
    if (!this.gebiet) return nothing;
    return html`
      <h2>${msg(str`So hat ${this._titelGebiet} gewählt`, { id: "app.wahl.block.titel" })}</h2>
      <p class="hinweis">
        ${msg("Für dieses Gebiet liegt kein Wahlergebnis im Bestand.", {
          id: "app.start.wahlen.leer",
        })}
      </p>
      ${stadtweitZurueck(this.gefiltert)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-wahl-gebietsblock": AppWahlGebietsblock;
  }
}
